/**
 * Sorts a refusal of the master account board, of the master account or of its own books into the
 * `masterAccountBoardShell.errors.*` translation key suffix.
 */
import { CombinedGraphQLErrors } from '@apollo/client';
import { GraphQLErrorCode } from '@insio/validation';

export const MasterAccountBoardErrorReason = {
  /** The own books were to be disconnected, left or transferred. */
  OWN_BOOKS_CANNOT_LEAVE: 'ownBooksCannotLeave',
  /** Revoking the master account while its own books still hold data or people. */
  OWN_BOOKS_NOT_EMPTY: 'ownBooksNotEmpty',
  /** Deletion requested or cancelled from inside the own books. */
  OWN_BOOKS_FOLLOWS_MASTER_ACCOUNT: 'ownBooksFollowsMasterAccount',
  /** The change would leave the master account without an active master admin. */
  LAST_MASTER_ADMIN: 'lastMasterAdmin',
  /** Ending the master account while it still has clients other than its own books. */
  MASTER_ACCOUNT_HAS_CLIENTS: 'masterAccountHasClients',
  /** The master account is scheduled for deletion, so it takes on no clients. */
  MASTER_ACCOUNT_PENDING_DELETION: 'masterAccountPendingDeletion'
} as const;

export type MasterAccountBoardErrorReason =
  (typeof MasterAccountBoardErrorReason)[keyof typeof MasterAccountBoardErrorReason];

const REASON_BY_CODE: Readonly<Record<string, MasterAccountBoardErrorReason>> = {
  [GraphQLErrorCode.OWN_BOOKS_CANNOT_LEAVE]: MasterAccountBoardErrorReason.OWN_BOOKS_CANNOT_LEAVE,
  [GraphQLErrorCode.OWN_BOOKS_NOT_EMPTY]: MasterAccountBoardErrorReason.OWN_BOOKS_NOT_EMPTY,
  [GraphQLErrorCode.OWN_BOOKS_FOLLOWS_MASTER_ACCOUNT]:
    MasterAccountBoardErrorReason.OWN_BOOKS_FOLLOWS_MASTER_ACCOUNT,
  [GraphQLErrorCode.LAST_MASTER_ADMIN]: MasterAccountBoardErrorReason.LAST_MASTER_ADMIN,
  [GraphQLErrorCode.MASTER_ACCOUNT_HAS_CLIENTS]:
    MasterAccountBoardErrorReason.MASTER_ACCOUNT_HAS_CLIENTS,
  [GraphQLErrorCode.MASTER_ACCOUNT_PENDING_DELETION]:
    MasterAccountBoardErrorReason.MASTER_ACCOUNT_PENDING_DELETION
};

/** The reason behind `error`, or `undefined` when it is none of the refusals above. */
export function masterAccountBoardErrorReason(
  error: unknown
): MasterAccountBoardErrorReason | undefined {
  if (!CombinedGraphQLErrors.is(error)) return undefined;

  for (const gqlError of error.errors) {
    const code = gqlError.extensions?.['code'];
    if (typeof code === 'string' && Object.hasOwn(REASON_BY_CODE, code)) {
      return REASON_BY_CODE[code];
    }
  }

  return undefined;
}
